import { z } from "zod";

/**
 * Employer subscription billing contract (`/api/billing/*`).
 * See billing-subscription-plan/DESIGN.md §3.
 */

export const subscriptionStatusSchema = z.enum([
  "none",
  "trialing",
  "active",
  "past_due",
  "unpaid",
  "canceled",
  "incomplete",
  "incomplete_expired",
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const billingTierSchema = z.object({
  key: z.string(),
  minWorkers: z.number(),
  maxWorkers: z.number().nullable(),
});
export type BillingTier = z.infer<typeof billingTierSchema>;

export const billingPaymentMethodSchema = z.object({
  type: z.enum(["card", "us_bank_account"]),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
});
export type BillingPaymentMethod = z.infer<typeof billingPaymentMethodSchema>;

export const billingOpenInvoiceSchema = z.object({
  amountDueCents: z.number(),
  currency: z.string(),
  failureMessage: z.string().nullable(),
  hostedInvoiceUrl: z.string().nullable(),
  nextAttemptAt: z.string().nullable(),
});
export type BillingOpenInvoice = z.infer<typeof billingOpenInvoiceSchema>;

export const billingStatusSchema = z.object({
  enabled: z.boolean(),
  status: subscriptionStatusSchema,
  tier: billingTierSchema.nullable(),
  /** Tier that takes effect next billing cycle (roster changed this period). */
  pendingTier: billingTierSchema.nullable(),
  rosterSize: z.number(),
  priceAmountCents: z.number().nullable(),
  pendingPriceAmountCents: z.number().nullable(),
  currency: z.string().nullable(),
  /** ISO instant. */
  currentPeriodEnd: z.string().nullable(),
  /** ISO instant. */
  trialEnd: z.string().nullable(),
  /** Trial a new subscription would get (0 = none). */
  trialDays: z.number(),
  paymentMethod: billingPaymentMethodSchema.nullable(),
  /** e.g. ACH micro-deposits awaiting verification. */
  paymentMethodPending: z.boolean(),
  /** Payment problem → show the resolution page. */
  actionRequired: z.boolean(),
  /** No dismissals left / canceled → the owner app is blocked. */
  blocked: z.boolean(),
  dismissalsRemaining: z.number(),
  openInvoice: billingOpenInvoiceSchema.nullable(),
  // Owner-initiated cancellation (Stripe portal, cancel at period end).
  // Defaulted so responses from a backend without these fields still parse.
  /** Still trialing/active, but ends at `cancelAt`. */
  cancelAtPeriodEnd: z.boolean().catch(false),
  /** ISO instant a pending cancellation takes effect. */
  cancelAt: z.string().nullable().catch(null),
  /** Why an ended (canceled / incomplete_expired) subscription ended. */
  cancellationReason: z.enum(["voluntary", "payment"]).nullable().catch(null),
});
export type BillingStatus = z.infer<typeof billingStatusSchema>;

export const setupIntentResponseSchema = z.object({
  clientSecret: z.string().min(1),
});
export type SetupIntentResponse = z.infer<typeof setupIntentResponseSchema>;

export const confirmSetupRequestSchema = z.object({
  setupIntentId: z.string().min(1),
});
export type ConfirmSetupRequest = z.infer<typeof confirmSetupRequestSchema>;

export const portalSessionRequestSchema = z.object({
  returnUrl: z.string().url(),
});
export type PortalSessionRequest = z.infer<typeof portalSessionRequestSchema>;

export const portalSessionResponseSchema = z.object({
  url: z.string().url(),
});
export type PortalSessionResponse = z.infer<typeof portalSessionResponseSchema>;

/** Error codes returned by `POST billing/resume`. */
export const BILLING_RESUME_ERROR_CODES = {
  /** 409 — no saved payment method. */
  paymentMethodRequired: "PAYMENT_METHOD_REQUIRED",
  /** 402 — the charge was declined (subscription left "incomplete"). */
  paymentFailed: "PAYMENT_FAILED",
} as const;

/** Body of the HTTP 402 any owner endpoint returns while the account is blocked. */
export const billingBlockedErrorSchema = z.object({
  code: z.literal("BILLING_BLOCKED"),
  message: z.string().optional(),
});
